'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import getComponentName from 'react-utils/getComponentName';
/**
 * @typedef {Object} GutterClickEventsOptions
 * @property {Function} onGutterClick       - Optional.
 *                                          Will be invoked before `onGutterClick` from props.
 *                                          Will be invoked for every instance of component.
 *                                          ({codemirror, lineNumber, gutterId, event}) => { }
 * @property {Function} onGutterContextMenu - Optional.
 *                                          Will be invoked before `onContextMenu` from props.
 *                                          Will be invoked for every instance of component.
 *                                          ({codemirror, lineNumber, gutterId, event}) => { }
 */

/**
 * @param  {GutterClickEventsOptions} options
 * @return {Function}
 */

export default function gutterClickEvents(options) {
  options = options || {};
  return function withGutterClickEvents(CodemirrorComponent) {
    /**
     * @class
     */
    class WithGutterClickEvents extends Component {
      constructor(props) {
        super(props);
        this.codemirror = null;
        this.refCodemirror = this.refCodemirror.bind(this);
        this.onGutterClick = this.onGutterClick.bind(this);
        this.onGutterContextMenu = this.onGutterContextMenu.bind(this);
      }

      componentWillUnmount() {
        this.bindEvents(false);
      }

      codemirrorMounted(codemirror) {
        if (this.codemirror === codemirror) {
          return;
        }

        this.codemirror = codemirror;

        if (!this.codemirror) {
          return;
        }

        this.bindEvents(true);
      }

      bindEvents(bind = true) {
        if (!this.codemirror) {
          return;
        }

        const method = bind ? 'on' : 'off';
        this.codemirror[method]('gutterClick', this.onGutterClick);
        this.codemirror[method]('gutterContextMenu', this.onGutterContextMenu);
      }
      /**
       * Fired on clicks anywhere in gutter.
       * @param  {Object} codemirror
       * @param  {Number} lineNumber
       * @param  {String} gutterId
       * @param  {Event}  event
       * @return {Undefined}
       */


      onGutterClick(codemirror, lineNumber, gutterId, event) {
        if (event.button === 2 || event.button === 0 && event.ctrl) {
          // CodeMirror is firing this event on any `mousedown`, including right-clicks.
          return;
        }

        const args = {
          codemirror,
          lineNumber,
          gutterId,
          event
        };

        if (options.onGutterClick) {
          options.onGutterClick(args);
        }

        if (this.props.onGutterClick) {
          this.props.onGutterClick(args);
        }
      }
      /**
       * Fired on contextmenu anywhere in gutter.
       * @param  {Object} codemirror
       * @param  {Number} lineNumber
       * @param  {String} gutterId
       * @param  {Event}  event
       * @return {Undefined}
       */


      onGutterContextMenu(codemirror, lineNumber, gutterId, event) {
        const args = {
          codemirror,
          lineNumber,
          gutterId,
          event
        };

        if (options.onGutterContextMenu) {
          options.onGutterContextMenu(args);
        }

        if (this.props.onGutterContextMenu) {
          this.props.onGutterContextMenu(args);
        }
      }

      refCodemirror(ref) {
        const codemirror = ref && ref.codemirror;
        this.codemirrorMounted(codemirror);

        if (this.props.refCodemirror) {
          this.props.refCodemirror(ref);
        }
      }

      render() {
        return /*#__PURE__*/_jsx(CodemirrorComponent, Object.assign({}, this.props, {
          refCodemirror: this.refCodemirror
        }));
      }

    }
    /*
     * Statics
     */


    WithGutterClickEvents.propTypes = {
      /** @type {Function} ({codemirror, lineNumber, gutterId, event}) => { } */
      onGutterClick: PropTypes.func,

      /** @type {Function} ({codemirror, lineNumber, gutterId, event}) => { } */
      onGutterContextMenu: PropTypes.func,

      /** @type {Function} */
      refCodemirror: PropTypes.func
    };
    WithGutterClickEvents.displayName = `WithGutterClickEvents(${getComponentName(CodemirrorComponent)})`;
    return WithGutterClickEvents;
  };
}